import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Check, Plus, Trash2, ClipboardList, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const formatDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Klassifiser dato: forfalt (rød), i dag (oransje), kommende (grå)
const dateStatus = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return { cls: 'bg-slate-100 text-slate-700', prefix: '' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  const diffDays = Math.round((date - today) / 86400000);
  if (diffDays < 0) return { cls: 'bg-red-100 text-red-700', prefix: 'Forfalt · ' };
  if (diffDays === 0) return { cls: 'bg-orange-100 text-orange-700', prefix: 'I dag · ' };
  return { cls: 'bg-slate-100 text-slate-700', prefix: '' };
};

// Sorter: oppdrag med dato først (tidligste øverst), deretter uten dato
const sortTasks = (tasks) => {
  return [...tasks].sort((a, b) => {
    const da = a.service.scheduled_date;
    const dbb = b.service.scheduled_date;
    if (da && dbb) return da.localeCompare(dbb);
    if (da) return -1;
    if (dbb) return 1;
    return 0;
  });
};

const emptyTask = {
  manual_customer_name: '',
  manual_customer_address: '',
  manual_customer_phone: '',
  notes: '',
  is_urgent: true,
  service_type: 'Akutt',
  scheduled_date: '',
};

export const ServiceTasksPanel = ({ onTasksChanged }) => {
  const navigate = useNavigate();
  const [urgentTasks, setUrgentTasks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const sortedCustomers = useMemo(() => [...customers].sort((a, b) => a.name.localeCompare(b.name, 'nb')), [customers]);
  const [showAddUrgent, setShowAddUrgent] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [inputMode, setInputMode] = useState('manual');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [newTask, setNewTask] = useState(emptyTask);

  useEffect(() => {
    fetchUrgentTasks();
    fetchCustomers();
  }, []);

  const fetchUrgentTasks = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/urgent-tasks`);
      setUrgentTasks(sortTasks(response.data || []));
    } catch (error) {
      setUrgentTasks([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data || []);
    } catch (error) {
      setCustomers([]);
    }
  };

  const notifyChange = () => {
    fetchUrgentTasks();
    if (onTasksChanged) onTasksChanged();
  };

  const resetForm = () => {
    setNewTask(emptyTask);
    setSelectedCustomerId('');
    setInputMode('manual');
  };

  const handleCompleteUrgent = async (serviceId) => {
    try {
      await axios.put(`${API}/services/${serviceId}/complete`);
      toast.success('Oppdrag markert som fullført!');
      notifyChange();
    } catch (error) {
      toast.error('Kunne ikke fullføre oppdrag');
    }
  };

  const handleAddUrgentTask = async () => {
    if (inputMode === 'manual' && !newTask.manual_customer_name.trim()) {
      toast.error('Kundenavn er påkrevd');
      return;
    }
    if (inputMode === 'dropdown' && !selectedCustomerId) {
      toast.error('Velg en kunde');
      return;
    }

    try {
      let taskData = {
        notes: newTask.notes,
        is_urgent: newTask.is_urgent,
        service_type: newTask.service_type,
        scheduled_date: newTask.scheduled_date || null,
      };

      if (inputMode === 'dropdown' && selectedCustomerId) {
        const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
        taskData.customer_id = selectedCustomerId;
        taskData.manual_customer_name = selectedCustomer?.name || '';
        taskData.manual_customer_address = selectedCustomer?.address || '';
        taskData.manual_customer_phone = selectedCustomer?.phone || '';
      } else {
        taskData.manual_customer_name = newTask.manual_customer_name;
        taskData.manual_customer_address = newTask.manual_customer_address;
        taskData.manual_customer_phone = newTask.manual_customer_phone;
      }

      await axios.post(`${API}/urgent-tasks`, taskData);
      toast.success('Oppdrag opprettet!');
      setShowAddUrgent(false);
      resetForm();
      notifyChange();
    } catch (error) {
      toast.error('Kunne ikke opprette oppdrag');
    }
  };

  const handleDeleteUrgentTask = async () => {
    if (!deleteTaskId) return;
    try {
      await axios.delete(`${API}/urgent-tasks/${deleteTaskId}`);
      toast.success('Oppdrag slettet!');
      setDeleteTaskId(null);
      notifyChange();
    } catch (error) {
      toast.error('Kunne ikke slette oppdrag');
    }
  };

  return (
    <>
      <Card id="tasks-section" className="border-slate-200" data-testid="service-tasks-panel">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-slate-700" />
              <CardTitle>Andre oppdrag</CardTitle>
            </div>
            <Button
              data-testid="add-urgent-task-button"
              onClick={() => setShowAddUrgent(true)}
              className="bg-slate-800 hover:bg-slate-900 text-white"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              Nytt oppdrag
            </Button>
          </div>
          {urgentTasks.length > 0 && (
            <CardDescription>
              {urgentTasks.filter(t => t.service.is_urgent).length} akutte, {urgentTasks.filter(t => !t.service.is_urgent).length} vanlige oppdrag
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {urgentTasks.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">Ingen aktive oppdrag</p>
          ) : (
            <div className="space-y-2">
              {urgentTasks.map((task) => (
                <div
                  key={task.service.id}
                  data-testid={`urgent-task-${task.service.id}`}
                  className={`px-3 py-2 bg-white rounded-lg border ${task.service.is_urgent ? 'border-orange-200' : 'border-slate-200'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-slate-900">{task.customer.name}</span>
                        {task.service.is_urgent ? (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-700 rounded-full shrink-0">Akutt</span>
                        ) : (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-brand-100 text-brand-700 rounded-full shrink-0">{task.service.service_type || 'Vanlig'}</span>
                        )}
                        {task.service.scheduled_date && (() => {
                          const ds = dateStatus(task.service.scheduled_date);
                          return (
                            <span
                              data-testid={`task-date-${task.service.id}`}
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded-full shrink-0 ${ds.cls}`}
                            >
                              <CalendarDays className="h-3 w-3" />
                              {ds.prefix}{formatDate(task.service.scheduled_date)}
                            </span>
                          );
                        })()}
                      </div>
                      {task.customer.address && <p className="text-xs text-slate-400 mt-0.5">{task.customer.address}</p>}
                      {task.service.notes && <p className="text-xs text-slate-400 italic mt-0.5">&ldquo;{task.service.notes}&rdquo;</p>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {task.customer.id && (
                        <Button variant="outline" size="sm" onClick={() => navigate(`/customers/${task.customer.id}`)} className="text-slate-700 h-7 text-xs px-2 hidden sm:inline-flex">Se kunde</Button>
                      )}
                      <Button data-testid={`complete-task-${task.service.id}`} size="sm" onClick={() => handleCompleteUrgent(task.service.id)} className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs px-2">
                        <Check className="h-3.5 w-3.5" /><span className="ml-1 hidden sm:inline">Fullført</span>
                      </Button>
                      <Button variant="outline" size="sm"
                        data-testid={`delete-task-${task.service.id}`}
                        onClick={() => setDeleteTaskId(task.service.id)}
                        className="text-red-600 border-red-200 hover:bg-red-50 h-7 px-1.5"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog for å legge til oppdrag */}
      <Dialog open={showAddUrgent} onOpenChange={(open) => {
        setShowAddUrgent(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nytt oppdrag</DialogTitle>
            <DialogDescription className="text-slate-600">
              Legg til et nytt serviceoppdrag.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type oppdrag</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={newTask.is_urgent ? 'default' : 'outline'}
                  onClick={() => setNewTask({...newTask, is_urgent: true, service_type: 'Akutt'})}
                  className={newTask.is_urgent ? 'bg-orange-600 hover:bg-orange-700 text-white' : ''}
                  size="sm"
                  data-testid="task-type-urgent"
                >
                  <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                  Akutt
                </Button>
                <Button
                  type="button"
                  variant={!newTask.is_urgent ? 'default' : 'outline'}
                  onClick={() => setNewTask({...newTask, is_urgent: false, service_type: 'Vanlig'})}
                  className={!newTask.is_urgent ? 'bg-brand-600 hover:bg-brand-700 text-white' : ''}
                  size="sm"
                  data-testid="task-type-regular"
                >
                  <ClipboardList className="h-3.5 w-3.5 mr-1" />
                  Vanlig
                </Button>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant={inputMode === 'manual' ? 'default' : 'outline'}
                onClick={() => setInputMode('manual')}
                className={inputMode === 'manual' ? 'bg-slate-900 text-white' : ''}
                size="sm"
              >
                Skriv manuelt
              </Button>
              <Button
                type="button"
                variant={inputMode === 'dropdown' ? 'default' : 'outline'}
                onClick={() => setInputMode('dropdown')}
                className={inputMode === 'dropdown' ? 'bg-slate-900 text-white' : ''}
                size="sm"
              >
                Velg fra kundeliste
              </Button>
            </div>

            {inputMode === 'dropdown' ? (
              <div className="space-y-2">
                <Label>Velg kunde *</Label>
                <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                  <SelectTrigger data-testid="urgent-task-customer-select">
                    <SelectValue placeholder="Velg en kunde..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedCustomers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCustomerId && (
                  <div className="text-sm text-slate-500 mt-2 p-2 bg-slate-50 rounded">
                    {(() => {
                      const c = customers.find(c => c.id === selectedCustomerId);
                      return c ? (
                        <>
                          <p>{c.address}</p>
                          <p>Tlf: {c.phone}</p>
                        </>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="customer_name">Kundenavn *</Label>
                  <Input
                    id="customer_name"
                    data-testid="urgent-task-customer-name"
                    placeholder="Skriv inn kundenavn"
                    value={newTask.manual_customer_name}
                    onChange={(e) => setNewTask({...newTask, manual_customer_name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer_address">Adresse</Label>
                  <Input
                    id="customer_address"
                    data-testid="urgent-task-address"
                    placeholder="Skriv inn adresse"
                    value={newTask.manual_customer_address}
                    onChange={(e) => setNewTask({...newTask, manual_customer_address: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customer_phone">Telefon</Label>
                  <Input
                    id="customer_phone"
                    data-testid="urgent-task-phone"
                    placeholder="Skriv inn telefonnummer"
                    value={newTask.manual_customer_phone}
                    onChange={(e) => setNewTask({...newTask, manual_customer_phone: e.target.value})}
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="scheduled_date">Dato</Label>
              <Input
                id="scheduled_date"
                type="date"
                data-testid="urgent-task-date"
                value={newTask.scheduled_date}
                onChange={(e) => setNewTask({...newTask, scheduled_date: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Beskrivelse / notat</Label>
              <Textarea
                id="notes"
                data-testid="urgent-task-notes"
                placeholder="Beskriv oppdraget..."
                value={newTask.notes}
                onChange={(e) => setNewTask({...newTask, notes: e.target.value})}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUrgent(false)}>
              Avbryt
            </Button>
            <Button
              data-testid="submit-urgent-task-button"
              onClick={handleAddUrgentTask}
              className={newTask.is_urgent ? "bg-orange-600 hover:bg-orange-700 text-white" : "bg-brand-600 hover:bg-brand-700 text-white"}
            >
              Opprett oppdrag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bekreftelse dialog for sletting */}
      <AlertDialog open={!!deleteTaskId} onOpenChange={(open) => !open && setDeleteTaskId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Slett oppdrag?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              Er du sikker på at du vil slette dette oppdraget? Denne handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-task"
              onClick={handleDeleteUrgentTask}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Slett
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ServiceTasksPanel;
